import { useContext, useEffect, useState } from "react";
import { BackendContext } from '../Context/BackendContext';
import { useParams, useLocation } from 'react-router-dom';
import { ColorProvider } from "../Context/ColorContext";
import HighlightPickerModal from "../Components/HighlightPickerModal";
import BannerGradientPickerModal from "../Components/Profile/BannerGradientPickerModal";
import DrawProfileAvatarModal from "../Components/Profile/DrawProfileAvatarModal";
import {
  Avatar,
  Button,
  Skeleton,
  Input,
  Tooltip,
} from "@heroui/react";
import { Twitter, Instagram, Youtube, Twitch, Pencil } from 'lucide-react';
import AppNavbar from "../Components/AppNavbar";
import { getPlayerProgress } from "../helper/playerProgress";

const toItemsArray = (res) => Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);

async function urlToBase64Data(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const reader = new FileReader();
  return await new Promise((resolve, reject) => {
    reader.onloadend = () => {
      // reader.result = "data:image/png;base64,AAAA..."
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const UserProfile = () => {
  const backendContext = useContext(BackendContext);
  const { username } = useParams();
  const location = useLocation();
  const userSub = location.state?.userSub || null;
  //const { username, joinedDate, initials, emblems = [], highlights = [] } = user;
  const userProfile = backendContext?.userProfile;

  const [isLoading, setIsLoading] = useState(true);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [userNotFound, setUserNotFound] = useState(false);

  const [highlights, setHighlights] = useState([]); // [{ artworkId, order }...]
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bannerEditorOpen, setBannerEditorOpen] = useState(false);
  const [editingSocials, setEditingSocials] = useState(false);
  const [socialsDraft, setSocialsDraft] = useState({
    twitter: "",
    instagram: "",
    youtube: "",
    twitch: "",
    discord: "",
  });
  const [savingSocials, setSavingSocials] = useState(false);
  const [socialsError, setSocialsError] = useState("");
  
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const avatarBase64 = currentUserProfile?.avatarImageData || null; // adjust to your field name
  const N = currentUserProfile?.avatarSize ?? 8;
  const playerProgress = getPlayerProgress(currentUserProfile);


  const bannerFrom = userProfile?.bannerConfig?.bannerFrom ?? "#7C3AED";
  const bannerTo = userProfile?.bannerConfig?.bannerTo ?? "#0EA5E9";
  const bannerAngle = typeof userProfile?.bannerConfig?.bannerAngle === "number" ? userProfile.bannerConfig.bannerAngle : 45;

  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoading(true);

      let fetchedUser;
      if (userSub) {
        fetchedUser = await backendContext.fetchSpecificUserProfile(userSub);
      } else {
        fetchedUser = await backendContext.fetchUserByUsername(username);
        //fetchedUser = await backendContext.fetchUserByUsername(userProfile.sub);
      }
      //console.log("Fetched user profile:", fetchedUser.bannerConfig);
      //console.log("current user profile:", userProfile.bannerConfig);

      if (!fetchedUser) {
        setUserNotFound(true);
        setIsLoading(false);
        return;
      }

      setCurrentUserProfile(fetchedUser);

      // seed socials draft
      const s = fetchedUser?.socials || {};
      setSocialsDraft({
        twitter: s.twitter || "",
        instagram: s.instagram || "",
        youtube: s.youtube || "",
        twitch: s.twitch || "",
        discord: s.discord || "",
      });

      if(fetchedUser?.sub === userProfile?.sub) {
        setIsCurrentUser(true);
      }

      setIsLoading(false);
      //console.log("🔵 UserProfile mounted ", bannerFrom);
    }

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!currentUserProfile?.sub) return;
    (async () => {
      const raw = await backendContext.fetchUserHighlights(currentUserProfile.sub);
      const arr = toItemsArray(raw).sort(
        (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
      );
      setHighlights(arr);
    })();
  }, [currentUserProfile?.sub]);

  // normalize a social to a simple handle/identifier
  const normalizeSocial = (network, value) => {
    if (!value) return "";
    let v = value.trim();
    // strip leading @
    if (v.startsWith("@")) v = v.slice(1);
    // remove URL prefixes
    try {
      if (/^https?:\/\//i.test(v)) {
        const url = new URL(v);
        v = url.pathname.replace(/^\/+/, "");
        // special-case YouTube channel/@handle path forms
        // leave whatever comes after the first path segment
        if (url.hostname.includes("youtube")) {
          // e.g., /@handle or /channel/XYZ or /c/Name
          v = url.pathname.replace(/^\/+/, "");
        }
      }
    } catch {}
    // very light allowlist
    v = v.replace(/\s+/g, "");
    return v;
  };

  const handleSocialChange = (key, val) => {
    setSocialsDraft(prev => ({ ...prev, [key]: val }));
  };

  const saveSocials = async () => {
    setSavingSocials(true);
    setSocialsError("");

    const payload = {
      twitter: normalizeSocial("twitter", socialsDraft.twitter),
      instagram: normalizeSocial("instagram", socialsDraft.instagram),
      youtube: normalizeSocial("youtube", socialsDraft.youtube),
      twitch: normalizeSocial("twitch", socialsDraft.twitch),
      discord: normalizeSocial("discord", socialsDraft.discord),
    };

    try {
      await backendContext.updateSocials(payload);
      // refresh current view model
      setCurrentUserProfile(prev => ({
        ...prev,
        socials: payload,
      }));
      setEditingSocials(false);
    } catch (e) {
      console.error("saveSocials failed:", e);
      setSocialsError("Could not save socials. Please try again.");
    } finally {
      setSavingSocials(false);
    }
  };

  const cancelSocials = () => {
    const s = currentUserProfile?.socials || {};
    setSocialsDraft({
      twitter: s.twitter || "",
      instagram: s.instagram || "",
      youtube: s.youtube || "",
      twitch: s.twitch || "",
      discord: s.discord || "",
    });
    setEditingSocials(false);
    setSocialsError("");
  };

  const openEditHighlights = () => setPickerOpen(true);

  const handleSaveAvatar = async (base64Png) => {
    // Persist via your backend. Example:
    await backendContext.updateUserProfileAvatar(base64Png);
    // Then refresh local view model:
    setCurrentUserProfile(prev => prev
      ? { ...prev, avatarImageData: base64Png }
      : prev
    );
    setAvatarEditorOpen(false);
  };

  if (userNotFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-2">User Not Found</h1>
          <p className="text-gray-400">The user you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-x-hidden bg-slate-900">
      <div className="relative bg-gray-900 text-white rounded-xl shadow-md max-w-6xl mx-auto pt-20">
        <AppNavbar />
        <div className="rounded-xl bg-slate-800 p-4">
          {/* Banner */}
          <div
            className="relative rounded-t-xl h-40"
            style={{ background: `linear-gradient(${bannerAngle}deg, ${bannerFrom}, ${bannerTo})` }}
          >
            {isCurrentUser && (
              <div className="absolute top-4 right-4 z-10">
                <button
                  className="text-sm bg-black/30 hover:bg-black/50 px-2 py-2 rounded-xl text-white"
                  onClick={() => setBannerEditorOpen(true)}
                  aria-label="Edit banner gradient"
                >
                  <Pencil />
                </button>
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="relative z-20 -mt-12 px-2 flex items-center gap-6 mb-6">
            {/* Avatar */}
            {isLoading ? (
              <Skeleton className="w-16 h-16 rounded-full" />
            ) : (
              <>
              <Avatar 
                key={avatarBase64 ? "has-src" : "no-src"}
                className="w-36 h-32 text-5xl"
                isBordered
                src={avatarBase64 ? `data:image/png;base64,${avatarBase64}` : null}
                name={currentUserProfile.username?.substring(0, 2)} 
                style={{ imageRendering: "pixelated" }}
              />
              {isCurrentUser && (
                <div className="absolute bottom-0 left-24 z-10">
                  <button 
                    className="text-sm bg-black/30 hover:bg-black/50 px-2 py-2 rounded-xl text-white"
                    onClick={() => setAvatarEditorOpen(true)}
                    aria-label="Edit avatar"
                  >
                    <Pencil />
                  </button>
                </div>
              )}
              </>
            )}

            {/* Name & Meta */}
            <div className="relative top-2 right-2 w-full">
              {isLoading ? (
                <>
                  <Skeleton className="w-32 h-5 mb-2 rounded" />
                  <Skeleton className="w-24 h-4 rounded" />
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center w-full pr-4">
                    <h2 className="text-2xl font-semibold">{currentUserProfile.username}</h2>

                    {/* Social Icons */}
                    <div className="flex gap-3 text-zinc-300 ml-auto">
                      {currentUserProfile.socials?.twitter && (
                        <a
                          href={`https://twitter.com/${currentUserProfile.socials.twitter}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-400"
                        >
                          <Twitter className="w-5 h-5" />
                        </a>
                      )}
                      {currentUserProfile.socials?.instagram && (
                        <a
                          href={`https://instagram.com/${currentUserProfile.socials.instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-pink-400"
                        >
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      {currentUserProfile.socials?.youtube && (
                        <a
                          href={`https://youtube.com/${currentUserProfile.socials.youtube}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-red-500"
                        >
                          <Youtube className="w-5 h-5" />
                        </a>
                      )}
                      {currentUserProfile.socials?.twitch && (
                        <a
                          href={`https://twitch.tv/${currentUserProfile.socials.twitch}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-purple-400"
                        >
                          <Twitch className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Joined {currentUserProfile.createdAt 
                      ? new Date(currentUserProfile.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) 
                      : "Unknown"}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="rounded-full border border-indigo-500/40 bg-indigo-500/15 px-3 py-1 text-sm font-semibold text-indigo-100">
                      Level {playerProgress.level}
                    </div>
                    <div className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-sm text-zinc-300">
                      {playerProgress.totalPixels.toLocaleString()} total pixels placed
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Social Media */}
          <section className="mb-6 px-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-lg">🔗 Socials</h3>
              {isCurrentUser && !isLoading && (
                <button
                  size="sm"
                  className="text-sm bg-black/30 hover:bg-black/50 px-2 py-2 rounded-xl text-white"
                  onClick={() => setEditingSocials(true)}
                  isDisabled={editingSocials}
                >
                  <Pencil />
                </button>
              )}
            </div>

            {/* VIEW MODE (non-owner or not editing) */}
            {!editingSocials && (
              <div className="flex flex-wrap gap-3 text-zinc-300">
                {(!currentUserProfile?.socials ||
                  Object.values(currentUserProfile.socials).every(v => !v)) && (
                  <div className="text-zinc-400 text-sm">
                    {isCurrentUser ? "You haven’t added any socials yet." : "No socials."}
                  </div>
                )}

                {currentUserProfile?.socials?.twitter && (
                  <a
                    href={`https://twitter.com/${currentUserProfile.socials.twitter}`}
                    target="_blank" rel="noopener noreferrer"
                    className="hover:text-blue-400 underline underline-offset-4"
                  >
                    Twitter @{currentUserProfile.socials.twitter}
                  </a>
                )}
                {currentUserProfile?.socials?.instagram && (
                  <a
                    href={`https://instagram.com/${currentUserProfile.socials.instagram}`}
                    target="_blank" rel="noopener noreferrer"
                    className="hover:text-pink-400 underline underline-offset-4"
                  >
                    Instagram @{currentUserProfile.socials.instagram}
                  </a>
                )}
                {currentUserProfile?.socials?.youtube && (
                  <>
                    <Youtube className="w-5 h-5" />
                    <a
                      href={`https://youtube.com/${currentUserProfile.socials.youtube.startsWith('@') ? currentUserProfile.socials.youtube : currentUserProfile.socials.youtube}`}
                      target="_blank" rel="noopener noreferrer"
                      className="hover:text-red-500 underline underline-offset-4"
                    >
                      YouTube {currentUserProfile.socials.youtube.startsWith('@') ? currentUserProfile.socials.youtube : `/${currentUserProfile.socials.youtube}`}
                    </a>
                  </>
                )}
                {currentUserProfile?.socials?.twitch && (
                  <a
                    href={`https://twitch.tv/${currentUserProfile.socials.twitch}`}
                    target="_blank" rel="noopener noreferrer"
                    className="hover:text-purple-400 underline underline-offset-4"
                  >
                    Twitch @{currentUserProfile.socials.twitch}
                  </a>
                )}
              </div>
            )}

            {/* EDIT MODE (owner) */}
            {editingSocials && isCurrentUser && (
              <div className="rounded-xl border border-slate-700 p-3 bg-slate-800/40">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    label="Twitter"
                    placeholder="@yourhandle or url"
                    value={socialsDraft.twitter}
                    onChange={(e) => handleSocialChange("twitter", e.target.value)}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <Input
                    label="Instagram"
                    placeholder="@yourhandle or url"
                    value={socialsDraft.instagram}
                    onChange={(e) => handleSocialChange("instagram", e.target.value)}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <Input
                    label="YouTube"
                    placeholder="@handle or channel path"
                    value={socialsDraft.youtube}
                    onChange={(e) => handleSocialChange("youtube", e.target.value)}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                  <Input
                    label="Twitch"
                    placeholder="@yourhandle or url"
                    value={socialsDraft.twitch}
                    onChange={(e) => handleSocialChange("twitch", e.target.value)}
                    variant="bordered"
                    labelPlacement="outside"
                  />
                </div>

                {socialsError && (
                  <div className="text-red-400 text-sm mt-2">{socialsError}</div>
                )}

                <div className="flex gap-2 justify-end mt-3">
                  <Button
                    className="bg-slate-700 text-white"
                    onPress={cancelSocials}
                    isDisabled={savingSocials}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="bg-indigo-600 text-white"
                    onPress={saveSocials}
                    isLoading={savingSocials}
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* Emblems Section */}
          {/*
          <section className="mb-10">
            <h3 className="font-semibold text-lg mb-3">🎖 Awards</h3>
            <div className="grid grid-cols-10 gap-4 max-w-full">
              {Array.from({ length: 10 }).map((_, idx) => (
                <div
                  key={idx}
                  className="w-24 h-24 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center"
                >
                  {//emblems[idx] ? emblems[idx] : ""
                  }
                </div>
              ))}
            </div>
          </section>
          */}
          {/* Highlights Section */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">⭐ Highlights</h3>
                {isCurrentUser && (
                  <button
                    className="text-sm bg-black/30 hover:bg-black/50 px-2 py-2 rounded-xl text-white"
                    onClick={openEditHighlights}
                  >
                    <Pencil />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => {
                const item = highlights.find(h => h.order === i) || highlights[i];
                if (!item) {
                  return (
                    <div key={i}
                      className="aspect-square bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-500 text-3xl">
                      {isCurrentUser ? "+" : ""}
                    </div>
                  );
                }
                return (
                  <HighlightTile
                    key={item.artworkId}
                    artworkId={item.artworkId}
                    backendContext={backendContext}
                    // click opens owner/guest modal (from earlier helper)
                    //onClick={() => openArtworkModal(item.artworkId)} 
                  />
                );
              })}
            </div>

            {isCurrentUser && (
              <HighlightPickerModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                backendContext={backendContext}
                initialSelection={highlights.map(h => h.artworkId)}
                  onSave={async (artworkIds) => {
                    const resp = await backendContext.setMyHighlights(artworkIds);
                    const arr = toItemsArray(resp).sort(
                      (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
                    );
                    setHighlights(arr);
                    setPickerOpen(false);
                  }}
              />
            )}

            {isCurrentUser && (
              <BannerGradientPickerModal
                isOpen={bannerEditorOpen}
                onClose={() => setBannerEditorOpen(false)}
                initial={{ from: bannerFrom, to: bannerTo, angle: bannerAngle }}
                onSave={async ({ from, to, angle }) => {
                  // Persist to backend (preferred)
                   await backendContext.updateUserBannerConfig(from, to, angle);
                }}
              />
            )}

            {isCurrentUser && avatarEditorOpen && (
              <ColorProvider>
              <DrawProfileAvatarModal
                isOpen={avatarEditorOpen}
                onCloseModal={() => setAvatarEditorOpen(false)}
                onSave={handleSaveAvatar}
                profileSize={N} // default to 8 if not set
                initialBase64={avatarBase64 ?? null}
                storageKey={`artparcel:avatar:${currentUserProfile?.sub || "me"}`}
              />
              </ColorProvider>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

function HighlightTile({ artworkId, backendContext, onClick }) {
  const [img, setImg] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const art = await backendContext.fetchArtwork(artworkId);
      // Prefer a lightweight field if you have it (e.g., art.thumbnail)
      // else use art.imageData (base64) as preview
      const thumb = art?.thumbnail ?? art?.imageData ?? null;
      if (alive) setImg(thumb ? `data:image/png;base64,${thumb}` : null);
    })();
    return () => { alive = false; };
  }, [artworkId]);

  return (
    <button
      onClick={onClick}
      className="aspect-square bg-gray-800 border border-gray-700 rounded overflow-hidden hover:ring-2 hover:ring-indigo-500 transition"
    >
      {img ? (
        <img
          src={img}
          alt=""
          className="w-full h-full object-contain"
          draggable={false}
          style={{ imageRendering: 'pixelated', border: '1px solid black' }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-500">…</div>
      )}
    </button>
  );
}

export default UserProfile;
